// src/components/admin/AuthorizationManagement.js
import React, { useState, useEffect } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import logger from '../../utils/logger';
import { 
  Key, Lock, RefreshCw, AlertTriangle, 
  CheckCircle, Copy, Calendar, Clock, Info,
  ArrowRight, ExternalLink, ShieldCheck, FileText,
  Eye, EyeOff
} from 'lucide-react';

/**
 * AuthorizationManagement Component - Manage OAuth authentication settings
 * Handles token management, refreshing, and monitoring
 */
const AuthorizationManagement = ({ bcService }) => {
  const [authData, setAuthData] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);
  const [showTokens, setShowTokens] = useState(false);
  const [refreshing, setRefreshing] = useState(false);
  const [newAuthModalOpen, setNewAuthModalOpen] = useState(false);
  const [copySuccess, setCopySuccess] = useState('');
  
  const navigate = useNavigate();
  
  // Fetch authentication data on component mount
  useEffect(() => {
    const fetchAuthData = async () => {
      try {
        setIsLoading(true);
        setError(null);
        
        // Check URL parameters for direct navigation from callback
        const searchParams = new URLSearchParams(window.location.search);
        const status = searchParams.get('status');
        const tokenReceived = searchParams.get('tokenReceived');
        const urlRegistrationId = searchParams.get('registrationId');
        const timestamp = searchParams.get('timestamp');
        
        // If we have URL parameters, log them
        if (status || tokenReceived || urlRegistrationId) {
          logger.log('=========== URL PARAMETERS DETECTED ===========');
          logger.log(`- Status: ${status || 'Not provided'}`);
          logger.log(`- Token received: ${tokenReceived || 'Not provided'}`);
          logger.log(`- Registration ID: ${urlRegistrationId || 'Not provided'}`);
          logger.log(`- Timestamp: ${timestamp || 'Not provided'}`);
          logger.log('==============================================');
          
          // Store registration ID from URL if available
          if (urlRegistrationId) {
            logger.log(`Setting registration ID from URL: ${urlRegistrationId}`);
            localStorage.setItem('registrationId', urlRegistrationId);
          }
          
          // Extract additional parameters for detailed diagnostics
          const tokenStoredInBC = searchParams.get('tokenStoredInBC');
          const diagnosticsParam = searchParams.get('diagnostics');
          let diagnosticsData = null;
          
          // Try to parse diagnostics JSON
          if (diagnosticsParam) {
            try {
              diagnosticsData = JSON.parse(diagnosticsParam);
              logger.log('Diagnostics data from URL:', diagnosticsData);
            } catch (err) {
              logger.warn('Failed to parse diagnostics data:', err);
            }
          }
          
          // Show status message based on URL parameters with more detailed diagnostics
          if (status === 'success' && tokenReceived === 'true' && tokenStoredInBC === 'true') {
            setError({
              type: 'success',
              message: 'Authorization completed and tokens stored successfully in Business Central!'
            });
          } else if (status === 'success' && tokenReceived === 'true' && tokenStoredInBC === 'false') {
            setError({
              type: 'warning',
              message: 'Authorization completed and tokens stored in memory, but failed to store in Business Central. Some features may not work correctly.'
            });
          } else if (status === 'success' && tokenReceived === 'false') {
            setError({
              type: 'error',
              message: 'Authorization started but token exchange or storage failed. Please check server logs and try again.'
            });
          } else if (status === 'error') {
            const errorMessage = searchParams.get('message') || 'Authorization failed. Please try again.';
            setError({
              type: 'error',
              message: errorMessage
            });
          }
          
          // Clear the URL parameters to avoid confusion on refresh
          if (window.history && window.history.replaceState) {
            const cleanUrl = window.location.pathname;
            window.history.replaceState({}, document.title, cleanUrl);
          }
        }
        
        // Fetch auth data from BC service
        logger.log('Fetching authorization status...');
        const response = await bcService.getAuthorizationStatus();
        logger.log('Auth status response:', response);
        setAuthData(response);
        
        // Show warning if tokens are not present
        if (response && !response.diagnostics?.hasAccessToken && status === 'success') {
          logger.warn('URL indicates success but no tokens found in backend');
          setError({
            type: 'warning',
            message: 'Authorization completed but tokens may not have been stored correctly. Please try again.'
          });
        }
      } catch (error) {
        logger.error('Error fetching authorization data:', error);
        setError({
          type: 'error',
          message: 'Failed to load authorization data. Please try again.'
        });
        
        // Create mock data for development
        if (process.env.NODE_ENV === 'development') {
          const mockAuthData = {
            isAuthorized: true,
            status: 'active',
            tokenType: 'Bearer',
            accessToken: 'eyJhbGciOiJIUzI1NiIsInR5cCI6IkpXVCJ9...',
            refreshToken: 'eyJhbGciOiJIUzI1NiIsInR5cCI6IkpXVCJ9...',
            expiresAt: new Date(Date.now() + 86400000 * 87).toISOString(), // 87 days from now
            scope: 'https://api.businesscentral.dynamics.com/.default',
            clientId: 'demoClientID',
            tenantId: 'demotenantId',
            issuedAt: new Date(Date.now() - 3600000 * 24 * 3).toISOString(), // 3 days ago
            environment: 'Production',
            diagnostics: {
              hasAccessToken: true,
              hasRefreshToken: true
            }
          };
          
          setAuthData(mockAuthData);
        }
      } finally {
        setIsLoading(false);
      }
    };
    
    fetchAuthData();
  }, [bcService, navigate]);
  
  // Handle token refresh
  const handleRefreshToken = async () => {
    try {
      setRefreshing(true);
      setError(null);
      
      // Call BC service to refresh token
      const result = await bcService.refreshAuthorizationToken();
      
      // Update auth data
      setAuthData(result);
      
      // Show success message
      setError({
        type: 'success',
        message: 'Token refreshed successfully!'
      });
    } catch (error) {
      logger.error('Error refreshing token:', error);
      setError({
        type: 'error',
        message: error.message || 'Failed to refresh token. Please try again.'
      });
    } finally {
      setRefreshing(false);
    }
  };
  
  // Handle new authorization
  const handleNewAuthorization = async () => {
    try {
      logger.log('=========== STARTING NEW AUTHORIZATION FLOW ===========');
      logger.log(`Authorization flow started at: ${new Date().toISOString()}`);
      logger.log(`Authorization flow timestamp: ${Date.now()}`);
      
      setIsLoading(true);
      setError(null);
      
      logger.log('Requesting authorization URL from backend service');
      // Get the authorization URL from the backend
      const response = await bcService.getAuthorizationUrl();
      
      logger.log('=========== AUTHORIZATION URL RECEIVED ===========');
      logger.log(`Response received at: ${new Date().toISOString()}`);
      
      // Extract authUrl from response - new method returns object with authUrl and registrationId
      const authUrl = response?.authUrl;
      
      if (!authUrl) {
        logger.error('No authorization URL received from backend');
        throw new Error('Failed to get authorization URL from the server.');
      }
      
      logger.log('Authorization URL details:');
      logger.log(`- Auth URL received: ${authUrl.length} characters long`);
      logger.log(`- Auth URL domain: ${new URL(authUrl).hostname}`);
      logger.log(`- Auth URL path: ${new URL(authUrl).pathname}`);
      logger.log(`- Registration ID: ${response.registrationId || 'Not provided'}`);
      
      // Store registration ID in localStorage for callback
      if (response.registrationId) {
        logger.log(`Storing registration ID in localStorage: ${response.registrationId}`);
        localStorage.setItem('registrationId', response.registrationId);
      } else {
        logger.warn('No registration ID provided in authorization URL response');
      }
      
      // Verify the URL has all needed parameters
      logger.log('Validating authorization URL parameters:');
      
      // Check for PKCE parameters
      const hasPkce = authUrl.includes('code_challenge=');
      logger.log(`- Has PKCE code_challenge: ${hasPkce ? 'YES' : 'NO'}`);
      
      if (!hasPkce) {
        logger.error('CRITICAL ERROR: Authorization URL is missing PKCE code_challenge - this will cause AADSTS9002325 errors');
        logger.error('This error occurs when the backend does not implement PKCE correctly or registration ID is missing');
        setError({
          type: 'error',
          message: 'PKCE setup failed. Authorization URL is missing required parameters.'
        });
        throw new Error('PKCE setup failed. Authorization URL is missing required parameters.');
      } else {
        logger.log('PKCE parameters verified ✓');
      }
      
      // Check consent parameter
      const hasConsent = authUrl.includes('prompt=consent');
      logger.log(`- Has prompt=consent: ${hasConsent ? 'YES' : 'NO'}`);
      
      // Check response_mode parameter
      const hasResponseMode = authUrl.includes('response_mode=');
      logger.log(`- Has response_mode: ${hasResponseMode ? 'YES' : 'NO'}`);
      
      // Check redirect_uri parameter
      const hasRedirectUri = authUrl.includes('redirect_uri=');
      logger.log(`- Has redirect_uri: ${hasRedirectUri ? 'YES' : 'NO'}`);
      
      // Check state parameter with registrationId
      const hasState = authUrl.includes('state=');
      logger.log(`- Has state parameter: ${hasState ? 'YES' : 'NO'}`);
      
      let finalAuthUrl = authUrl;
      
      // Add consent parameter if missing
      if (!hasConsent) {
        logger.warn('Authorization URL does not include prompt=consent, adding it');
        const connector = authUrl.includes('?') ? '&' : '?';
        finalAuthUrl = `${authUrl}${connector}prompt=consent`;
        logger.log('Modified URL to add consent prompt parameter');
      }
      
      logger.log('=========== OPENING AUTHORIZATION WINDOW ===========');
      logger.log(`Opening auth window at: ${new Date().toISOString()}`);
      logger.log(`Auth window features: width=800,height=600`);
      
      // Open the authorization flow in a new window
      const authWindow = window.open(finalAuthUrl, '_blank', 'width=800,height=600');
      
      // Check if the window was blocked
      if (!authWindow) {
        logger.error('Auth window was blocked by the browser - popup blocker may be enabled');
        throw new Error('The authorization window was blocked by your browser. Please allow popups for this site.');
      } else {
        logger.log('Auth window opened successfully ✓');
      }
      
      // Log PKCE status from backend response
      if (response.pkceEnabled) {
        logger.log('PKCE is enabled for this authorization flow ✓');
      } else {
        logger.warn('WARNING: PKCE is not enabled - this may cause cross-origin issues!');
      }
      
      logger.log('=========== SETTING UP MESSAGE LISTENER ===========');
      logger.log('Waiting for postMessage from auth window...');
      
      // Set up a message listener to detect when the popup completes
      const messageHandler = (event) => {
        logger.log('=========== AUTH WINDOW MESSAGE RECEIVED ===========');
        logger.log(`Message received at: ${new Date().toISOString()}`);
        logger.log('Message data:', event.data);
        logger.log(`Message origin: ${event.origin}`);
        
        // Check if this is our auth success message
        if (event.data && event.data.type === 'AUTH_SUCCESS') {
          logger.log('Authorization completed successfully via postMessage ✓');
          logger.log(`Registration ID in message: ${event.data.registrationId || 'Not provided'}`);
          logger.log(`Message timestamp: ${event.data.timestamp || 'Not provided'}`);
          
          // Store registration ID from message if provided
          if (event.data.registrationId) {
            logger.log(`Updating registration ID from message: ${event.data.registrationId}`);
            localStorage.setItem('registrationId', event.data.registrationId);
          }
          
          // Remove the event listener
          window.removeEventListener('message', messageHandler);
          logger.log('Message event listener removed');
          
          // Show success message
          setError({
            type: 'success',
            message: 'Authorization completed successfully! Refreshing data...'
          });
          
          logger.log('=========== REFRESHING AUTH DATA ===========');
          logger.log(`Refresh started at: ${new Date().toISOString()}`);
          
          // Refresh the auth data
          bcService.getAuthorizationStatus().then(response => {
            logger.log('Auth data refresh successful ✓');
            logger.log('Auth data:', response);
            
            // Check that tokens were actually received and stored
            const hasAccessToken = response.diagnostics?.hasAccessToken;
            const hasRefreshToken = response.diagnostics?.hasRefreshToken;
            const tokenStoredInBC = response.diagnostics?.tokenStoredInBC === true;
            const isAuthorized = response.isAuthorized === true;
            
            logger.log('=========== TOKEN VALIDATION RESULTS ===========');
            logger.log(`- Access token present: ${hasAccessToken ? 'YES' : 'NO'}`);
            logger.log(`- Refresh token present: ${hasRefreshToken ? 'YES' : 'NO'}`);
            logger.log(`- Token stored in BC: ${tokenStoredInBC ? 'YES' : 'NO'}`);
            logger.log(`- Is authorized: ${isAuthorized ? 'YES' : 'NO'}`);
            
            if (response.diagnostics) {
              logger.log('Additional diagnostics:');
              logger.log(`- Token expires at: ${response.diagnostics.tokenExpiresAt || 'unknown'}`);
              logger.log(`- Token issued at: ${response.diagnostics.tokenIssuedAt || 'unknown'}`);
              logger.log(`- Registration ID: ${response.diagnostics.registrationId || 'unknown'}`);
              logger.log(`- Environment: ${response.diagnostics.environment || 'unknown'}`);
              logger.log(`- BC environment used: ${response.diagnostics.bcEnvironmentUsed || 'unknown'}`);
            }
            logger.log('===============================================');
            
            // Update state with response data
            setAuthData(response);
            
            if (isAuthorized && hasAccessToken && tokenStoredInBC) {
              // Complete success - tokens were received and stored in both memory and BC
              setError({
                type: 'success',
                message: 'Authorization completed and tokens stored successfully in Business Central!'
              });
            } else if (isAuthorized && hasAccessToken && !tokenStoredInBC) {
              // Partial success - tokens in memory but not in BC
              logger.warn('============ BC TOKEN STORAGE FAILED ============');
              logger.warn('Auth tokens are available in memory but not stored in Business Central');
              logger.warn('Some features requiring BC token storage may not work correctly');
              logger.warn('Diagnostics:', response.diagnostics);
              
              setError({
                type: 'warning',
                message: 'Authorization completed and tokens stored in memory, but failed to store in Business Central. Some features may not work correctly.'
              });
            } else if (response.diagnostics && !hasAccessToken) {
              // We got a response but no tokens
              logger.error('============ TOKEN STORAGE FAILED ============');
              logger.error('Auth status returned successfully but no access token was found');
              logger.error('This indicates the token exchange completed but token storage failed');
              logger.error('Diagnostics:', response.diagnostics);
              
              setError({
                type: 'error',
                message: 'Authorization started but token storage failed. Please check server logs and try again.'
              });
            } else {
              // Partial success - the request completed but authorization status is unclear
              setError({
                type: 'warning',
                message: 'Authorization status is unclear. Please check logs for errors and refresh the page.'
              });
            }
          }).catch(error => {
            logger.error('=========== AUTH DATA REFRESH ERROR ===========');
            logger.error('Error refreshing auth data:', error.message);
            logger.error('Error details:', error);
            
            setError({
              type: 'error',
              message: 'Authorization may have failed. Unable to verify token status. Please refresh and try again.'
            });
          });
        } else {
          logger.log('Ignoring message - not an AUTH_SUCCESS message');
        }
      };
      
      // Add the event listener for postMessage communication
      window.addEventListener('message', messageHandler);
      
      // Close the modal
      setNewAuthModalOpen(false);
      
      // Show a success message to guide the user
      setError({
        type: 'success',
        message: 'Authorization window opened. Please complete the login process in the new window.'
      });
    } catch (error) {
      logger.error('Error starting authorization flow:', error);
      setError({
        type: 'error',
        message: error.message || 'Failed to start authorization flow. Please try again.'
      });
    } finally {
      setIsLoading(false);
    }
  };
  
  // Copy token to clipboard
  const copyToClipboard = (text, type) => {
    navigator.clipboard.writeText(text).then(
      () => {
        setCopySuccess(`${type} copied!`);
        setTimeout(() => setCopySuccess(''), 2000);
      },
      () => {
        setCopySuccess('Failed to copy!');
      }
    );
  };
  
  // Format date for display
  const formatDate = (dateString) => {
    if (!dateString) return 'Unknown';
    const date = new Date(dateString);
    return date.toLocaleDateString('en-GB', {
      day: '2-digit',
      month: 'short',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };
  
  // Calculate days remaining until token expiration
  const getDaysRemaining = (expiresAt) => {
    if (!expiresAt) return 'Unknown';
    
    const now = new Date();
    const expiration = new Date(expiresAt);
    const diffTime = expiration - now;
    const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
    
    return diffDays;
  };
  
  // Calculate time remaining until token expiration
  const getTimeRemainingText = (expiresAt) => {
    if (!expiresAt) return 'Unknown';
    
    const days = getDaysRemaining(expiresAt);
    
    if (days <= 0) {
      return 'Expired';
    } else if (days === 1) {
      return '1 day remaining';
    } else {
      return `${days} days remaining`;
    }
  };
  
  // Get status badge for auth status
  const getStatusBadge = (status) => {
    if (!status) return null;
    
    switch (status.toLowerCase()) {
      case 'active':
        return (
          <span className="px-2 inline-flex text-xs leading-5 font-semibold rounded-full bg-green-100 text-green-800">
            Active
          </span>
        );
      case 'expired':
        return (
          <span className="px-2 inline-flex text-xs leading-5 font-semibold rounded-full bg-red-100 text-red-800">
            Expired
          </span>
        );
      case 'warning':
        return (
          <span className="px-2 inline-flex text-xs leading-5 font-semibold rounded-full bg-yellow-100 text-yellow-800">
            Warning
          </span>
        );
      default:
        return (
          <span className="px-2 inline-flex text-xs leading-5 font-semibold rounded-full bg-gray-100 text-gray-800">
            {status}
          </span>
        );
    }
  };
  
  // Modal for new authorization
  const NewAuthorizationModal = () => {
    return (
      <div className="fixed z-10 inset-0 overflow-y-auto">
        <div className="flex items-end justify-center min-h-screen pt-4 px-4 pb-20 text-center sm:block sm:p-0">
          <div className="fixed inset-0 transition-opacity" aria-hidden="true">
            <div className="absolute inset-0 bg-gray-500 opacity-75"></div>
          </div>
          
          <span className="hidden sm:inline-block sm:align-middle sm:h-screen" aria-hidden="true">&#8203;</span>
          
          <div className="inline-block align-bottom bg-white rounded-lg text-left overflow-hidden shadow-xl transform transition-all sm:my-8 sm:align-middle sm:max-w-lg sm:w-full">
            <div className="bg-white px-4 pt-5 pb-4 sm:p-6 sm:pb-4">
              <div className="sm:flex sm:items-start">
                <div className="mx-auto flex-shrink-0 flex items-center justify-center h-12 w-12 rounded-full bg-indigo-100 sm:mx-0 sm:h-10 sm:w-10">
                  <Key className="h-6 w-6 text-indigo-600" />
                </div>
                <div className="mt-3 text-center sm:mt-0 sm:ml-4 sm:text-left">
                  <h3 className="text-lg leading-6 font-medium text-gray-900">New Authorization</h3>
                  <div className="mt-2">
                    <p className="text-sm text-gray-500">
                      You are about to start a new authorization flow with Microsoft. This will allow the application to access Business Central on behalf of your users.
                    </p>
                  </div>
                </div>
              </div>
              
              <div className="mt-6 p-4 bg-blue-50 rounded-md">
                <div className="flex">
                  <div className="flex-shrink-0">
                    <Info className="h-5 w-5 text-blue-400" />
                  </div>
                  <div className="ml-3">
                    <h3 className="text-sm font-medium text-blue-800">Administrator privileges required</h3>
                    <div className="mt-2 text-sm text-blue-700">
                      <p>
                        You will need to log in with a Business Central admin account to authorize the application. Make sure you have the necessary permissions before proceeding.
                      </p>
                    </div>
                  </div>
                </div>
              </div>
            </div>
            
            <div className="bg-gray-50 px-4 py-3 sm:px-6 sm:flex sm:flex-row-reverse">
              <button
                type="button"
                onClick={handleNewAuthorization}
                className="w-full inline-flex justify-center rounded-md border border-transparent shadow-sm px-4 py-2 bg-indigo-600 text-base font-medium text-white hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500 sm:ml-3 sm:w-auto sm:text-sm"
              >
                Start Authorization
              </button>
              <button
                type="button"
                onClick={() => setNewAuthModalOpen(false)}
                className="mt-3 w-full inline-flex justify-center rounded-md border border-gray-300 shadow-sm px-4 py-2 bg-white text-base font-medium text-gray-700 hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500 sm:mt-0 sm:ml-3 sm:w-auto sm:text-sm"
              >
                Cancel
              </button>
            </div>
          </div>
        </div>
      </div>
    );
  };
  
  // Display loading state
  if (isLoading && !authData) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-indigo-500"></div>
      </div>
    );
  }
  
  return (
    <div>
      {/* Header */}
      <div className="md:flex md:items-center md:justify-between mb-6">
        <div className="flex-1 min-w-0">
          <h2 className="text-2xl font-bold leading-7 text-gray-900 sm:text-3xl sm:truncate">
            Authentication Management
          </h2>
          <p className="mt-1 text-sm text-gray-500">
            Manage OAuth authentication for Business Central
          </p>
        </div>
        <div className="mt-4 flex md:mt-0 md:ml-4">
          <button
            type="button"
            onClick={handleRefreshToken}
            disabled={refreshing}
            className="inline-flex items-center px-4 py-2 border border-gray-300 rounded-md shadow-sm text-sm font-medium text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500"
          >
            {refreshing ? (
              <>
                <RefreshCw className="animate-spin -ml-1 mr-2 h-5 w-5 text-gray-500" />
                Refreshing...
              </>
            ) : (
              <>
                <RefreshCw className="-ml-1 mr-2 h-5 w-5 text-gray-500" />
                Refresh Token
              </>
            )}
          </button>
          <button
            type="button"
            onClick={() => setNewAuthModalOpen(true)}
            className="ml-3 inline-flex items-center px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-indigo-600 hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500"
          >
            <Key className="-ml-1 mr-2 h-5 w-5" />
            New Authorization
          </button>
        </div>
      </div>
      
      {/* Error/Success message */}
      {error && (
        <div className={`mb-6 p-4 rounded-md ${
          error.type === 'success' ? 'bg-green-50 border border-green-200' : 'bg-red-50 border border-red-200'
        }`}>
          <div className="flex">
            <div className="flex-shrink-0">
              {error.type === 'success' ? (
                <CheckCircle className="h-5 w-5 text-green-400" />
              ) : (
                <AlertTriangle className="h-5 w-5 text-red-400" />
              )}
            </div>
            <div className="ml-3">
              <p className={`text-sm ${
                error.type === 'success' ? 'text-green-700' : 'text-red-700'
              }`}>
                {error.message}
              </p>
            </div>
          </div>
        </div>
      )}
      
      {/* Authorization Status */}
      <div className="bg-white shadow overflow-hidden sm:rounded-lg mb-8">
        <div className="px-4 py-5 sm:px-6 flex justify-between items-center">
          <div>
            <h3 className="text-lg leading-6 font-medium text-gray-900">
              Authorization Status
            </h3>
            <p className="mt-1 max-w-2xl text-sm text-gray-500">
              Current OAuth authorization details
            </p>
          </div>
          <div>
            {authData && getStatusBadge(authData.status)}
          </div>
        </div>
        
        {!authData?.isAuthorized ? (
          <div className="border-t border-gray-200 px-4 py-12 sm:px-6 text-center">
            <ShieldCheck className="mx-auto h-12 w-12 text-gray-400" />
            <h3 className="mt-2 text-lg font-medium text-gray-900">Not Authorized</h3>
            <p className="mt-1 text-sm text-gray-500">
              The application is not currently authorized to access Business Central.
            </p>
            <div className="mt-6">
              <button
                type="button"
                onClick={() => setNewAuthModalOpen(true)}
                className="inline-flex items-center px-4 py-2 border border-transparent shadow-sm text-sm font-medium rounded-md text-white bg-indigo-600 hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500"
              >
                <Key className="-ml-1 mr-2 h-5 w-5" />
                Start Authorization
              </button>
            </div>
          </div>
        ) : (
          <div className="border-t border-gray-200">
            <dl>
              <div className="bg-gray-50 px-4 py-5 sm:grid sm:grid-cols-3 sm:gap-4 sm:px-6">
                <dt className="text-sm font-medium text-gray-500">Client ID</dt>
                <dd className="mt-1 text-sm text-gray-900 sm:mt-0 sm:col-span-2">{authData.clientId}</dd>
              </div>
              <div className="bg-white px-4 py-5 sm:grid sm:grid-cols-3 sm:gap-4 sm:px-6">
                <dt className="text-sm font-medium text-gray-500">Tenant ID</dt>
                <dd className="mt-1 text-sm text-gray-900 sm:mt-0 sm:col-span-2">{authData.tenantId}</dd>
              </div>
              <div className="bg-gray-50 px-4 py-5 sm:grid sm:grid-cols-3 sm:gap-4 sm:px-6">
                <dt className="text-sm font-medium text-gray-500">Environment</dt>
                <dd className="mt-1 text-sm text-gray-900 sm:mt-0 sm:col-span-2">{authData.environment}</dd>
              </div>
              <div className="bg-white px-4 py-5 sm:grid sm:grid-cols-3 sm:gap-4 sm:px-6">
                <dt className="text-sm font-medium text-gray-500">Scope</dt>
                <dd className="mt-1 text-sm text-gray-900 sm:mt-0 sm:col-span-2">{authData.scope}</dd>
              </div>
              <div className="bg-gray-50 px-4 py-5 sm:grid sm:grid-cols-3 sm:gap-4 sm:px-6">
                <dt className="text-sm font-medium text-gray-500">Issued At</dt>
                <dd className="mt-1 text-sm text-gray-900 sm:mt-0 sm:col-span-2">{formatDate(authData.issuedAt)}</dd>
              </div>
              <div className="bg-white px-4 py-5 sm:grid sm:grid-cols-3 sm:gap-4 sm:px-6">
                <dt className="text-sm font-medium text-gray-500">Expires At</dt>
                <dd className="mt-1 text-sm text-gray-900 sm:mt-0 sm:col-span-2">
                  <div className="flex items-center">
                    <span className="mr-2">{formatDate(authData.expiresAt)}</span>
                    <span className={`px-2 inline-flex text-xs leading-5 font-semibold rounded-full ${
                      getDaysRemaining(authData.expiresAt) <= 7
                        ? 'bg-red-100 text-red-800'
                        : getDaysRemaining(authData.expiresAt) <= 30
                          ? 'bg-yellow-100 text-yellow-800'
                          : 'bg-green-100 text-green-800'
                    }`}>
                      {getTimeRemainingText(authData.expiresAt)}
                    </span>
                  </div>
                </dd>
              </div>
              <div className="bg-gray-50 px-4 py-5 sm:grid sm:grid-cols-3 sm:gap-4 sm:px-6">
                <dt className="text-sm font-medium text-gray-500">Access Token</dt>
                <dd className="mt-1 text-sm text-gray-900 sm:mt-0 sm:col-span-2">
                  <div className="flex items-center">
                    <div className="relative flex-1">
                      <input
                        type={showTokens ? "text" : "password"}
                        readOnly
                        value={authData.accessToken}
                        className="w-full pr-10 p-2 bg-gray-50 border-gray-300 rounded text-sm font-mono"
                      />
                      <button
                        type="button"
                        onClick={() => setShowTokens(!showTokens)}
                        className="absolute inset-y-0 right-0 pr-3 flex items-center text-gray-500 hover:text-gray-700"
                      >
                        {showTokens ? (
                          <EyeOff className="h-5 w-5" />
                        ) : (
                          <Eye className="h-5 w-5" />
                        )}
                      </button>
                    </div>
                    <button
                      onClick={() => copyToClipboard(authData.accessToken, 'Access token')}
                      className="ml-2 p-2 text-gray-500 hover:text-gray-700"
                    >
                      <Copy className="h-5 w-5" />
                    </button>
                    {copySuccess === 'Access token copied!' && (
                      <span className="ml-2 text-green-600 text-xs">{copySuccess}</span>
                    )}
                  </div>
                </dd>
              </div>
              <div className="bg-white px-4 py-5 sm:grid sm:grid-cols-3 sm:gap-4 sm:px-6">
                <dt className="text-sm font-medium text-gray-500">Refresh Token</dt>
                <dd className="mt-1 text-sm text-gray-900 sm:mt-0 sm:col-span-2">
                  <div className="flex items-center">
                    <div className="relative flex-1">
                      <input
                        type={showTokens ? "text" : "password"}
                        readOnly
                        value={authData.refreshToken}
                        className="w-full pr-10 p-2 bg-white border-gray-300 rounded text-sm font-mono"
                      />
                    </div>
                    <button
                      onClick={() => copyToClipboard(authData.refreshToken, 'Refresh token')}
                      className="ml-2 p-2 text-gray-500 hover:text-gray-700"
                    >
                      <Copy className="h-5 w-5" />
                    </button>
                    {copySuccess === 'Refresh token copied!' && (
                      <span className="ml-2 text-green-600 text-xs">{copySuccess}</span>
                    )}
                  </div>
                </dd>
              </div>
            </dl>
          </div>
        )}
      </div>
      
      {/* Authorization How-To Guide */}
      <div className="bg-white shadow overflow-hidden sm:rounded-lg">
        <div className="px-4 py-5 sm:px-6">
          <h3 className="text-lg leading-6 font-medium text-gray-900">
            How to Authorize Business Central
          </h3>
          <p className="mt-1 max-w-2xl text-sm text-gray-500">
            Guide to setting up and refreshing OAuth authorization
          </p>
        </div>
        <div className="border-t border-gray-200 px-4 py-5 sm:px-6">
          <div className="prose prose-indigo max-w-none">
            <h4>Authorization Process Overview</h4>
            <p>
              To connect the portal to Business Central, you need to authorize the application using the OAuth 2.0 Authorization Code flow. This allows the portal to access Business Central API on behalf of your users.
            </p>
            
            <ol className="list-decimal pl-5 space-y-2">
              <li className="pl-2">Click on the "New Authorization" button at the top of this page.</li>
              <li className="pl-2">You will be redirected to Microsoft login page.</li>
              <li className="pl-2">Sign in with your Business Central admin account.</li>
              <li className="pl-2">Review and accept the permissions requested by the application.</li>
              <li className="pl-2">After authorization, you will be redirected back to the portal.</li>
            </ol>
            
            <h4 className="mt-6">Token Management</h4>
            <p>
              The application uses two types of tokens:
            </p>
            
            <ul className="list-disc pl-5 space-y-2">
              <li className="pl-2">
                <strong>Access Token:</strong> Used to make API calls to Business Central. Valid for 1 hour.
              </li>
              <li className="pl-2">
                <strong>Refresh Token:</strong> Used to obtain new access tokens without requiring user interaction. Valid for 90 days.
              </li>
            </ul>
            
            <p className="mt-4">
              The application automatically refreshes access tokens as needed. However, you should manually refresh the tokens if:
            </p>
            
            <ul className="list-disc pl-5 space-y-2">
              <li className="pl-2">You changed permissions or settings in Business Central.</li>
              <li className="pl-2">The refresh token is about to expire (less than 7 days remaining).</li>
              <li className="pl-2">You encounter authorization errors in the portal.</li>
            </ul>
            
            <div className="mt-6 p-4 bg-yellow-50 rounded-md">
              <div className="flex">
                <div className="flex-shrink-0">
                  <AlertTriangle className="h-5 w-5 text-yellow-400" />
                </div>
                <div className="ml-3">
                  <h5 className="text-sm font-medium text-yellow-800">Important</h5>
                  <div className="mt-2 text-sm text-yellow-700">
                    <p>
                      If the refresh token expires, you will need to perform a full authorization again. We recommend refreshing tokens at least once a month to prevent expiration.
                    </p>
                  </div>
                </div>
              </div>
            </div>
            
            <h4 className="mt-6">Additional Resources</h4>
            <ul className="list-disc pl-5 space-y-2">
              <li className="pl-2">
                <a 
                  href="https://learn.microsoft.com/en-us/dynamics365/business-central/dev-itpro/administration/automation-apis-using-s2s-authentication" 
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-indigo-600 hover:text-indigo-500 flex items-center"
                >
                  Microsoft Documentation on Business Central API Authentication
                  <ExternalLink className="ml-1 h-3 w-3" />
                </a>
              </li>
              <li className="pl-2">
                <a 
                  href="#" 
                  className="text-indigo-600 hover:text-indigo-500 flex items-center"
                >
                  SiriusConnect365 Authentication Guide
                  <FileText className="ml-1 h-3 w-3" />
                </a>
              </li>
            </ul>
          </div>
        </div>
      </div>
      
      {/* New Authorization Modal */}
      {newAuthModalOpen && <NewAuthorizationModal />}
    </div>
  );
};

export default AuthorizationManagement;